import React, { useState, useEffect } from 'react';
import { FiX, FiInfo } from 'react-icons/fi';
import { FaLink, FaImage } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { CURRENCY_SYMBOLS, CURRENCY_SYMBOLS_TEXT } from '../../utils/currencySymbols';
import InfoModal from './InfoModal';

const ItemModal = ({ isOpen, onClose, onSubmit, profileId, editingItem, categories, updateCategories, currencies = ['USD'] }) => {
  // Get primary currency (first in user's selection)
  const primaryCurrency = currencies[0] || 'USD';

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    priceCurrency: primaryCurrency,
    quantity: '',
    category: null,
    sku: '',
    costPrice: '',
    costPriceCurrency: primaryCurrency,
    supplier: '',
    location: '',
    notes: '',
    productUrl: '',      // eBay or other product link
    imageUrl: '',        // Direct image URL
    needsRestock: true   // Toggle for restock alerts (default ON)
  });
  
  const [showProductUrlInfo, setShowProductUrlInfo] = useState(false);
  const [showImageUrlInfo, setShowImageUrlInfo] = useState(false);

  // Create currency options from user's selected currencies only
  const currencyOptions = currencies.map(curr => ({
    value: curr,
    label: CURRENCY_SYMBOLS_TEXT[curr]
  }));

  useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name || '',
        price: editingItem.price || '',
        priceCurrency: editingItem.priceCurrency || primaryCurrency,
        quantity: editingItem.quantity || '',
        category: editingItem.category 
          ? { value: editingItem.category, label: editingItem.category }
          : null,
        sku: editingItem.sku || '',
        costPrice: editingItem.costPrice || '',
        costPriceCurrency: editingItem.costPriceCurrency || primaryCurrency,
        supplier: editingItem.supplier || '',
        location: editingItem.location || '',
        notes: editingItem.notes || '',
        productUrl: editingItem.productUrl || '',
        imageUrl: editingItem.imageUrl || '',
        needsRestock: editingItem.needsRestock !== undefined ? editingItem.needsRestock : true
      });
    } else {
      setFormData({
        name: '',
        price: '',
        priceCurrency: primaryCurrency,
        quantity: '',
        category: null,
        sku: '',
        costPrice: '',
        costPriceCurrency: primaryCurrency,
        supplier: '',
        location: '',
        notes: '',
        productUrl: '',
        imageUrl: '',
        needsRestock: true
      });
    }
  }, [editingItem, isOpen, primaryCurrency]);

  const categoryOptions = categories
    .map(cat => ({ value: cat, label: cat }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleCreateCategory = async (inputValue) => {
    const newCategory = inputValue.trim();
    if (newCategory && !categories.includes(newCategory)) {
      const newCategories = [...categories, newCategory];
      await updateCategories(newCategories);
      
      setFormData(prev => ({
        ...prev,
        category: { value: newCategory, label: newCategory }
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const itemData = {
      profileId,
      name: formData.name,
      price: parseFloat(formData.price) || 0,
      priceCurrency: formData.priceCurrency,
      quantity: parseInt(formData.quantity) || 0,
      category: formData.category?.value || '',
      sku: formData.sku,
      costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
      costPriceCurrency: formData.costPriceCurrency,
      supplier: formData.supplier,
      location: formData.location,
      notes: formData.notes,
      productUrl: formData.productUrl,
      imageUrl: formData.imageUrl,
      needsRestock: formData.needsRestock,
      createdAt: editingItem?.createdAt || new Date(),
      updatedAt: new Date()
    };

    onSubmit(itemData);
    
    setFormData({
      name: '',
      price: '',
      priceCurrency: primaryCurrency,
      quantity: '',
      category: null,
      sku: '',
      costPrice: '',
      costPriceCurrency: primaryCurrency,
      supplier: '',
      location: '',
      notes: '',
      productUrl: '',
      imageUrl: '',
      needsRestock: true
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left 
              overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle 
              sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto relative z-[70]"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingItem ? 'Edit Item' : 'Add Item'}
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Product Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price *
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-1">
                        <Select
                          options={currencyOptions}
                          value={currencyOptions.find(opt => opt.value === formData.priceCurrency)}
                          onChange={(selected) => setFormData({ ...formData, priceCurrency: selected.value })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              backgroundColor: 'var(--select-bg)',
                              borderColor: state.isFocused ? '#14b8a6' : 'var(--select-border)',
                              boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 184, 166, 0.2)' : 'none',
                              '&:hover': {
                                borderColor: '#14b8a6'
                              },
                              minHeight: '42px'
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: 'var(--select-menu-bg)',
                              zIndex: 100
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused ? 'var(--select-option-hover)' : 'transparent',
                              color: 'var(--select-text)',
                              cursor: 'pointer'
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: 'var(--select-text)'
                            })
                          }}
                          isSearchable={false}
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                            {CURRENCY_SYMBOLS[formData.priceCurrency]}
                          </span>
                          <input
                            type="number"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            onBlur={(e) => {
                              // Format to 2 decimal places when leaving the input
                              if (e.target.value && !isNaN(e.target.value)) {
                                setFormData({
                                  ...formData,
                                  price: Number(e.target.value).toFixed(2)
                                });
                              }
                            }}
                            className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                              dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                              focus:border-transparent transition-colors"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantity (Optional)
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                        dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                        focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <CreatableSelect
                      isClearable
                      value={formData.category}
                      onChange={(newValue) => setFormData({ ...formData, category: newValue })}
                      onCreateOption={handleCreateCategory}
                      options={categoryOptions}
                      placeholder="Select or create category..."
                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          backgroundColor: 'var(--select-bg)',
                          borderColor: state.isFocused ? '#14b8a6' : 'var(--select-border)',
                          boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 184, 166, 0.2)' : 'none',
                          '&:hover': {
                            borderColor: '#14b8a6'
                          }
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: 'var(--select-menu-bg)',
                          zIndex: 100
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused ? 'var(--select-option-hover)' : 'transparent',
                          color: 'var(--select-text)',
                          cursor: 'pointer'
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: 'var(--select-text)'
                        }),
                        input: (base) => ({
                          ...base,
                          color: 'var(--select-text)'
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: 'var(--select-placeholder)'
                        })
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      SKU
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., SKU-123"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                        dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                        focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cost Price
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-1">
                        <Select
                          options={currencyOptions}
                          value={currencyOptions.find(opt => opt.value === formData.costPriceCurrency)}
                          onChange={(selected) => setFormData({ ...formData, costPriceCurrency: selected.value })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              backgroundColor: 'var(--select-bg)',
                              borderColor: state.isFocused ? '#14b8a6' : 'var(--select-border)',
                              boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 184, 166, 0.2)' : 'none',
                              '&:hover': {
                                borderColor: '#14b8a6'
                              },
                              minHeight: '42px'
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: 'var(--select-menu-bg)',
                              zIndex: 100
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused ? 'var(--select-option-hover)' : 'transparent',
                              color: 'var(--select-text)',
                              cursor: 'pointer'
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: 'var(--select-text)'
                            })
                          }}
                          isSearchable={false}
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                            {CURRENCY_SYMBOLS[formData.costPriceCurrency]}
                          </span>
                          <input
                            type="number"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            value={formData.costPrice}
                            onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                            onBlur={(e) => {
                              // Format to 2 decimal places when leaving the input
                              if (e.target.value && !isNaN(e.target.value)) {
                                setFormData({
                                  ...formData,
                                  costPrice: Number(e.target.value).toFixed(2)
                                });
                              }
                            }}
                            className="w-full pl-8 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                              dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                              focus:border-transparent transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Supplier
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Supplier Name"
                      value={formData.supplier}
                      onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                        dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                        focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Warehouse A, Shelf 3"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                  />
                </div>

                {/* Product URL */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaLink className="mr-2 text-gray-500" />
                    Product URL (Optional)
                    <button
                      type="button"
                      onClick={() => setShowProductUrlInfo(true)}
                      className="ml-2 p-1 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-full transition-colors"
                    >
                      <FiInfo className="h-4 w-4 text-teal-500 dark:text-teal-400 cursor-pointer animate-pulse" />
                    </button>
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.ebay.com/itm/..."
                    value={formData.productUrl}
                    onChange={(e) => setFormData({ ...formData, productUrl: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                  />
                </div>

                {/* Image URL with helpful tooltip */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaImage className="mr-2 text-gray-500" />
                    Image URL (Optional)
                    <button
                      type="button"
                      onClick={() => setShowImageUrlInfo(true)}
                      className="ml-2 p-1 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-full transition-colors"
                    >
                      <FiInfo className="h-4 w-4 text-teal-500 dark:text-teal-400 cursor-pointer animate-pulse" />
                    </button>
                  </label>
                  <input
                    type="url"
                    placeholder="https://i.ebayimg.com/images/..."
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors"
                  />
                  {/* Image Preview */}
                  {formData.imageUrl && (
                    <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                      <img 
                        src={formData.imageUrl} 
                        alt="Product preview" 
                        className="max-h-32 rounded-lg object-contain mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <p className="text-xs text-red-600 dark:text-red-400 text-center mt-2" style={{display: 'none'}}>
                        ⚠️ Image failed to load. Please check the URL.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes
                  </label>
                  <textarea
                    placeholder="Additional notes..."
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                      dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-teal-500 
                      focus:border-transparent transition-colors resize-none"
                  />
                </div>

                {/* Restock Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable Restock Alerts
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Get notified when this item is low on stock
                    </p>
                  </div>
                  
                  {/* iOS-style Toggle Slider */}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, needsRestock: !formData.needsRestock })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ml-4
                      ${formData.needsRestock ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                    title={formData.needsRestock ? "Restock alerts ON" : "Restock alerts OFF"}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${formData.needsRestock ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 
                      dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 
                      text-white rounded-lg hover:shadow-lg transition-all font-medium"
                  >
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Info Modals */}
      <InfoModal
        isOpen={showProductUrlInfo}
        onClose={() => setShowProductUrlInfo(false)}
        title="📋 Product URL"
        content={
          <div>
            <p className="font-semibold mb-2">Paste your product page link</p>
            <p className="text-sm text-white/90 mb-2">
              This can be an eBay listing, Amazon product page, or any online product link.
            </p>
            <p className="text-sm text-white/90">
              The link will be accessible via the "View Product Page" button on your item cards.
            </p>
            <p className="text-sm text-white/90 mt-2">
              Example: <span className="font-mono text-yellow-300">https://www.ebay.com/itm/...</span>
            </p>
          </div>
        }
      />

      <InfoModal
        isOpen={showImageUrlInfo}
        onClose={() => setShowImageUrlInfo(false)}
        title="🖼️ Product Image URL"
        content={
          <div>
            <p className="font-semibold mb-3">How to get image URL:</p>
            <ol className="list-decimal list-inside space-y-2 text-white/95">
              <li>Right-click on the product image</li>
              <li>Select <span className="font-semibold text-yellow-300">"Copy Image Address"</span></li>
              <li>Paste the URL here ✨</li>
            </ol>
            <p className="text-sm text-white/90 mt-4">
              The image will be displayed on your item cards. If no image URL is provided, a default icon will be shown.
            </p>
          </div>
        }
      />
    </AnimatePresence>
  );
};

export default ItemModal;

